import { apiOperation, headers } from "./api";
import type {
  CarteiraExposicao,
  CarteiraFrescor,
  HistoricoFornecedor,
  MonitoramentoDigest,
  MonitoramentoResumo,
  MonitoramentoSerasaDistribuicaoDto,
  MonitoramentoSerasaListaCarteiraRespostaDto,
  MonitoramentoSerasaEventosRespostaDto,
  RelatorioSemanalPayload,
} from "../models/monitoramentoSerasa/types";

const PREFIX = "monitoramento-serasa";

const config = () => ({ headers: headers() });

export async function getDigest(params?: {
  /** Instante de referência (ex.: início do mês corrente em ISO) para comparativo mensal no digest. */
  dataReferenciaUtc?: string;
}): Promise<MonitoramentoDigest> {
  const { data } = await apiOperation.get<MonitoramentoDigest>(
    `${PREFIX}/digest`,
    params?.dataReferenciaUtc
      ? { ...config(), params: { dataReferenciaUtc: params.dataReferenciaUtc } }
      : config()
  );
  return data;
}

export async function getResumo(): Promise<MonitoramentoResumo> {
  const { data } = await apiOperation.get<MonitoramentoResumo>(
    `${PREFIX}/resumo`,
    config()
  );
  return data;
}

export interface EventosQuery {
  desdeUtc?: string;
  ateUtc?: string;
  pagina?: number;
  tamanhoPagina?: number;
  severidade?: number;
  cnpj?: string;
}

export async function getEventos(
  params: EventosQuery
): Promise<MonitoramentoSerasaEventosRespostaDto> {
  const { data } = await apiOperation.get<MonitoramentoSerasaEventosRespostaDto>(
    `${PREFIX}/eventos`,
    { ...config(), params }
  );
  return data;
}

export interface CnpjsQuery {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  /** 0 | 1 | 2 conforme `SegmentoCarteiraMonitoramento`. */
  segmento?: number;
  /**
   * Case-insensitive: `valorTotalAnotacoes` (default), `data` / `dataConsulta`,
   * `razao` / `razaoSocial`, `score` / `pioresPrimeiro` (menor score primeiro).
   */
  ordenarPor?: string;
  precisaRevisao?: boolean;
}

export async function getCnpjs(
  params: CnpjsQuery
): Promise<MonitoramentoSerasaListaCarteiraRespostaDto> {
  const { data } = await apiOperation.get<MonitoramentoSerasaListaCarteiraRespostaDto>(
    `${PREFIX}/cnpjs`,
    { ...config(), params }
  );
  return data;
}

export async function getCarteiraExposicao(): Promise<CarteiraExposicao> {
  const { data } = await apiOperation.get<CarteiraExposicao>(
    `${PREFIX}/carteira/exposicao`,
    config()
  );
  return data;
}

export async function getCarteiraFrescor(): Promise<CarteiraFrescor> {
  const { data } = await apiOperation.get<CarteiraFrescor>(
    `${PREFIX}/carteira/frescor`,
    config()
  );
  return data;
}

export async function getCarteiraDistribuicao(): Promise<MonitoramentoSerasaDistribuicaoDto> {
  const { data } = await apiOperation.get<MonitoramentoSerasaDistribuicaoDto>(
    `${PREFIX}/carteira/distribuicao`,
    config()
  );
  return data;
}

export async function getHistoricoFornecedor(
  cnpj14: string,
  opts?: {
    incluirMarcos?: boolean;
    incluirSerie?: boolean;
    /** Série por mês-calendário (`serieMensal`); `false` reduz payload. Default API: `true`. */
    incluirSerieMensal?: boolean;
  }
): Promise<HistoricoFornecedor> {
  const params = {
    incluirMarcos: opts?.incluirMarcos ?? true,
    incluirSerie: opts?.incluirSerie ?? true,
    incluirSerieMensal: opts?.incluirSerieMensal ?? true,
  };
  const { data } = await apiOperation.get<HistoricoFornecedor>(
    `${PREFIX}/cnpjs/${cnpj14}/historico`,
    { ...config(), params }
  );
  return data;
}

export async function getRelatorioSemanal(
  periodoInicioUtc?: string,
  periodoFimUtc?: string
): Promise<RelatorioSemanalPayload> {
  const params: Record<string, string> = {};
  if (periodoInicioUtc) params.periodoInicioUtc = periodoInicioUtc;
  if (periodoFimUtc) params.periodoFimUtc = periodoFimUtc;
  const { data } = await apiOperation.get<RelatorioSemanalPayload>(
    `${PREFIX}/relatorio-semanal`,
    { ...config(), ...(Object.keys(params).length ? { params } : {}) }
  );
  return data;
}
